'use client'

import { useEffect, useRef, useState } from 'react'

// Module-level cache shared across all instances
const DEFAULT_CACHE_KEY = '__default__'
const _cache = new Map<string, string | null>()
const _promise = new Map<string, Promise<string | null>>()
const _listeners = new Set<(id: string | null, cacheKey: string) => void>()

async function _fetch(cacheKey: string): Promise<string | null> {
  const existingPromise = _promise.get(cacheKey)
  if (existingPromise) return existingPromise
  const promise = fetch('/api/brand/active')
    .then(r => (r.ok ? r.json() : null))
    .then(data => {
      const id: string | null = data?.brandId ?? null
      _cache.set(cacheKey, id)
      _promise.delete(cacheKey)
      _listeners.forEach(fn => fn(id, cacheKey))
      return id
    })
    .catch(() => {
      _cache.set(cacheKey, null)
      _promise.delete(cacheKey)
      return null
    })
  _promise.set(cacheKey, promise)
  return promise
}

/**
 * Update the module-level cache and notify all active useActiveBrand() instances.
 * Call this after switching brands so the sidebar link updates in real-time.
 *
 * @param newId  Pass the new brand ID to update immediately,
 *               or omit/undefined to force a re-fetch on next render.
 * @param workspaceId  Optional workspace cache key for workspace-switch-safe callers.
 */
export function invalidateActiveBrandCache(newId?: string | null, workspaceId?: string | null) {
  const cacheKey = workspaceId ?? DEFAULT_CACHE_KEY
  if (newId !== undefined) {
    _cache.set(cacheKey, newId)
    _listeners.forEach(fn => fn(newId, cacheKey))
  } else {
    _cache.delete(cacheKey)
    _promise.delete(cacheKey)
  }
}

export function useActiveBrand(workspaceId?: string | null, { enabled = true }: { enabled?: boolean } = {}): { activeBrandId: string | null; isLoading: boolean } {
  const cacheKey = workspaceId ?? DEFAULT_CACHE_KEY
  const [activeBrandId, setActiveBrandId] = useState<string | null>(_cache.get(cacheKey) ?? null)
  const [isLoading, setIsLoading] = useState(enabled && !_cache.has(cacheKey))
  const mountedRef = useRef(true)

  useEffect(() => {
    if (!enabled) {
      setActiveBrandId(null)
      setIsLoading(false)
      return
    }

    mountedRef.current = true
    let cancelled = false
    setIsLoading(!_cache.has(cacheKey))

    const listener = (id: string | null, updatedCacheKey: string) => {
      if (!mountedRef.current) return
      if (cancelled) return
      if (updatedCacheKey !== cacheKey) return
      setActiveBrandId(id)
      setIsLoading(false)
    }
    _listeners.add(listener)

    if (_cache.has(cacheKey)) {
      setActiveBrandId(_cache.get(cacheKey) ?? null)
      setIsLoading(false)
    } else {
      _fetch(cacheKey).then(id => {
        if (!mountedRef.current) return
        if (cancelled) return
        setActiveBrandId(id)
        setIsLoading(false)
      })
    }

    return () => {
      cancelled = true
      mountedRef.current = false
      _listeners.delete(listener)
    }
  }, [cacheKey])

  return { activeBrandId, isLoading }
}
